import { type FC, useRef } from 'react';
import { isValidHexColor } from './TeamConfig.utils';
import styles from './TeamConfig.module.scss';

interface ColorInputProps {
  value: string;
  onChange: (hex: string) => void;
  label: string;
  fieldName: string;
  error?: boolean;
  disabled?: boolean;
  testId?: string;
}

/**
 * Hex color input with an inline circular swatch that opens a native color picker on click.
 */
const ColorInput: FC<ColorInputProps> = ({
  value,
  onChange,
  label,
  fieldName,
  error,
  disabled,
  testId = 'color-input',
}) => {
  const colorRef = useRef<HTMLInputElement>(null);
  const showSwatch = isValidHexColor(value);

  return (
    <div className={styles.colorField} data-testid={testId}>
      <div className={styles.colorFieldHeader}>
        <span className="fw6 f6">{fieldName}</span>
        <span className="ml2 mid-gray f7">{label}</span>
      </div>
      <div className={styles.colorInputWrapper} data-testid={`${testId}-wrapper`}>
        <span
          role="button"
          aria-label={`Choose ${fieldName} color`}
          tabIndex={disabled ? -1 : 0}
          className={styles.inlineSwatch}
          style={{ backgroundColor: showSwatch ? value : '#ffffff' }}
          onClick={() => !disabled && colorRef.current?.click()}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              colorRef.current?.click();
            }
          }}
          data-testid={`${testId}-swatch`}
        />
        <input
          ref={colorRef}
          type="color"
          className={styles.hiddenColorPicker}
          value={showSwatch ? value : '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          tabIndex={-1}
          disabled={disabled}
        />
        <input
          type="text"
          className={`${styles.colorTextInput} ${error ? 'error' : ''}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          maxLength={7}
          disabled={disabled}
          aria-label={fieldName}
          aria-invalid={error === true}
          data-testid={`${testId}-input`}
        />
      </div>
    </div>
  );
};

export default ColorInput;
